import { CircularProgress, Tooltip } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import variables from "../../../../assets/styles/_variables.module.scss";
import NavigationBlockWithBadge from "./NavigationBlockWithBadge";
import NavigationButton from "./NavigationButton";

interface PendingImportsButtonProps {
    /** Number of pending imports */
    pendingCount: number;
    /** Click handler */
    onClick: () => void;
    /** Whether currently on imports history page */
    isOnHistoryPage?: boolean;
    /** Style variant */
    variant?: "default" | "book";
    /** Whether the navigation is collapsed */
    isCollapsed?: boolean;
}

/**
 * Button showing pending imports count with loading spinner
 * Shows only icon when collapsed
 */
const PendingImportsButton = ({
    pendingCount,
    onClick,
    isOnHistoryPage = false,
    variant = "default",
    isCollapsed = false,
}: PendingImportsButtonProps) => {
    const { t } = useTranslation();

    if (pendingCount === 0) {
        return null;
    }

    const buttonClassName = classNames({
        "navigation-link--collapsed": isCollapsed,
    });

    // Don't apply minWidth when collapsed
    const buttonStyle = isOnHistoryPage && !isCollapsed ? { minWidth: "216px" } : undefined;

    return (
        <Tooltip title={isCollapsed ? t("NavigationBar.PendingImports") : ""} placement="right" arrow>
            <span>
                <NavigationButton
                    onClick={onClick}
                    isActive={isOnHistoryPage}
                    variant={variant}
                    className={buttonClassName}
                    style={buttonStyle}
                >
                    <NavigationBlockWithBadge
                        icon={<CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />}
                        label={t("NavigationBar.PendingImports")}
                        badgeCount={pendingCount}
                        badgeTooltip={t("Notifications.UnreadTooltip", { number: pendingCount })}
                        isCollapsed={isCollapsed}
                    />
                </NavigationButton>
            </span>
        </Tooltip>
    );
};

export default PendingImportsButton;
